import React, { useRef } from "react";

/**
 * 6-box numeric one-time-code input. Auto-advances, handles backspace, and
 * accepts a pasted code. Value is the joined string; parent owns it.
 */
const OtpInput: React.FC<{
  value: string;
  onChange: (v: string) => void;
  onComplete?: (v: string) => void;
  length?: number;
  autoFocus?: boolean;
  disabled?: boolean;
}> = ({ value, onChange, onComplete, length = 6, autoFocus = true, disabled = false }) => {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = value.split("").slice(0, length);

  const set = (next: string) => {
    const clean = next.replace(/\D/g, "").slice(0, length);
    onChange(clean);
    if (clean.length === length) onComplete?.(clean);
  };

  const handleChange = (i: number, raw: string) => {
    const d = raw.replace(/\D/g, "");
    if (!d) return;
    const arr = value.split("");
    // typing into a box replaces that position, then fills forward from a paste
    for (let k = 0; k < d.length && i + k < length; k++) arr[i + k] = d[k];
    set(arr.join(""));
    const nextIdx = Math.min(i + d.length, length - 1);
    refs.current[nextIdx]?.focus();
  };

  const handleKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const arr = value.split("");
      if (arr[i]) {
        arr[i] = "";
        set(arr.join(""));
      } else if (i > 0) {
        arr[i - 1] = "";
        set(arr.join(""));
        refs.current[i - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && i > 0) {
      refs.current[i - 1]?.focus();
    } else if (e.key === "ArrowRight" && i < length - 1) {
      refs.current[i + 1]?.focus();
    }
  };

  return (
    <div className="flex gap-2 justify-center" onPaste={(e) => {
      e.preventDefault();
      set(e.clipboardData.getData("text"));
      refs.current[Math.min(e.clipboardData.getData("text").replace(/\D/g, "").length, length - 1)]?.focus();
    }}>
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={1}
          disabled={disabled}
          autoFocus={autoFocus && i === 0}
          value={digits[i] || ""}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onFocus={(e) => e.target.select()}
          className="w-11 h-12 border rounded text-center text-lg font-semibold focus:border-teal-500 focus:outline-none disabled:bg-gray-100"
        />
      ))}
    </div>
  );
};

export default OtpInput;
